import { CommonModule } from '@angular/common';
import {
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { AppFormsModule } from '@ideapa/app-forms';
import {
  AbstractSecurityStorage,
  AuthModule,
  LogLevel,
  StsConfigLoader,
  StsConfigStaticLoader,
} from 'angular-auth-oidc-client';
import { merge } from 'lodash';
import { localStorageSync } from 'ngrx-store-localstorage';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { AppUiKitModule } from '@ideapa/app-ui-kit';
import { EffectsModule } from '@ngrx/effects';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { AuthEffects } from './+state/auth.effects';
import { AuthFacade } from './+state/auth.facade';
import { AUTH_FEATURE_KEY, initialState, reducer } from './+state/auth.reducer';
import { AuthConfig } from './auth-config';
import { AuthService } from './auth.service';
import { ChangePasswordModalContainer } from './containers/change-password-modal/change-password-modal.container';
import { ChooseMfaMethodContainer } from './containers/choose-mfa-method/choose-mfa-method.container';
import { EmailOtpVerifyContainer } from './containers/email-otp-verify/email-otp-verify.container';
import { ForbiddenContainer } from './containers/forbidden/forbidden.container';
import { ForgotPasswordContainer } from './containers/forgot-password/forgot-password.container';
import { LoginRootContainer } from './containers/login-root/login-root.container';
import { LoginContainer } from './containers/login/login.container';
import { MicrosoftAuthenticatorSetupContainer } from './containers/microsoft-authenticator-setup/microsoft-authenticator-setup.container';
import { MicrosoftAuthenticatorVerifyContainer } from './containers/microsoft-authenticator-verify/microsoft-authenticator-verify.container';
import { ResetPasswordContainer } from './containers/reset-password/reset-password.container';
import { SelectModuloModalContainer } from './containers/select-modulo-modal/select-modulo-modal.container';
import { CustomStorage } from './custom-storage';
import { AADCallbackGuard } from './guards/aad-callback.guard';
import { httpInterceptorProviders } from './http-interceptor-providers';

export function configureAuth(config: AuthConfig) {
  return new StsConfigStaticLoader({
    // authority: `https://${config.aadB2cName}.b2clogin.com/${config.aadB2cName}.onmicrosoft.com/B2C_1_ASSET_MANAGER_LABS_LOGIN`,
    // authWellknownEndpointUrl: `https://${config.aadB2cName}.b2clogin.com/${config.aadB2cName}.onmicrosoft.com/B2C_1_ASSET_MANAGER_LABS_LOGIN/v2.0/.well-known/openid-configuration`,
    // redirectUrl: window.location.origin,
    // postLogoutRedirectUri: window.location.origin,
    // clientId: config.aadClientId, //'angularCodeRefreshTokens',
    // scope: `openid https://${config.aadB2cName}.onmicrosoft.com/d2218bc6-e4d9-4604-ae4c-488a527aa4d9/user_access offline_access`, //offline_access
    authority: `https://login.microsoftonline.com/${config.tenantId}/v2.0`,
    authWellknownEndpointUrl: `https://login.microsoftonline.com/${config.tenantId}/v2.0/.well-known/openid-configuration`,
    redirectUrl: window.location.origin, //  + '/auth/callback'
    clientId: config.clientId,
    scope:
      'openid profile offline_access email api://ca4716d7-ae42-4526-939d-98c60c130378/access_as_user',
    responseType: 'code',
    maxIdTokenIatOffsetAllowedInSeconds: 600,
    issValidationOff: true,
    autoUserInfo: false,
    useRefreshToken: true,
    logLevel: LogLevel.Debug,
    ignoreNonceAfterRefresh: true,
    customParamsAuthRequest: {
      prompt: 'select_account', // login, consent
    },
    // silentRenew: true,
    // tokenRefreshInSeconds: 3,
    // renewTimeBeforeTokenExpiresInSeconds: 2700, // Refresh dopo 15 minuti (45 pre-scadenza) per evitare che scada la session AD
    // renewTimeBeforeTokenExpiresInSeconds: 3500, // Refresh dopo 2 minuti (58 pre-scadenza) per evitare che scada la session AD
    // silentRenew: false,
    // silentRenewUrl: window.location.origin + '/silent-renew.html',
  });
}

const INIT_ACTION = '@ngrx/store/init';
const UPDATE_ACTION = '@ngrx/store/update-reducers';

export function localStorageSyncReducer(
  reducer: ActionReducer<any>
): ActionReducer<any> {
  return localStorageSync({
    keys: ['modulo', 'ente', 'anno'],
    rehydrate: true,
    removeOnUndefined: true,
    storageKeySerializer: (key) => AUTH_FEATURE_KEY + '_' + key,
    mergeReducer: (state, rehydratedState, action) => {
      // console.log('mergeReducer', state, rehydratedState, action);

      // https://github.com/btroncone/ngrx-store-localstorage/issues/93
      // https://github.com/btroncone/ngrx-store-localstorage/pull/76/files
      // N.B. questa funzione è necessaria altrimenti ogni volta che viene caricato un lazy module
      // lo stato viene rehydratato con la localstorage iniziale perdendo così eventuali widget aggiunti
      if (
        (action.type === INIT_ACTION ||
          (action.type === UPDATE_ACTION &&
            action.features.indexOf('auth') >= 0)) &&
        rehydratedState
      ) {
        try {
          state = merge(state, rehydratedState);
        } catch (err) {
          console.error(err);
          console.log(state);
          console.log(rehydratedState);
        }
      }
      return state;
    },
  })(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

@NgModule({
  imports: [
    AppUiKitModule,
    CommonModule,
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: configureAuth,
        deps: [AuthConfig],
      },
    }),
    StoreModule.forFeature(AUTH_FEATURE_KEY, reducer, {
      initialState: initialState,
      metaReducers,
    }),
    EffectsModule.forFeature([AuthEffects]),
    RouterModule.forChild([
      {
        path: 'forbidden',
        component: ForbiddenContainer,
      },
      {
        path: 'auth',
        component: LoginRootContainer,
        children: [
          {
            path: 'login',
            pathMatch: 'full',
            component: LoginContainer,
          },
          {
            path: 'forgot-password',
            pathMatch: 'full',
            component: ForgotPasswordContainer,
          },
          {
            path: 'reset-password',
            pathMatch: 'full',
            component: ResetPasswordContainer,
          },
          {
            path: 'callback',
            canActivate: [AADCallbackGuard],
            children: [],
          },
          {
            path: 'microsoft-authenticator-setup',
            pathMatch: 'full',
            component: MicrosoftAuthenticatorSetupContainer,
          },
          {
            path: 'microsoft-authenticator-verify',
            pathMatch: 'full',
            component: MicrosoftAuthenticatorVerifyContainer,
          },
          {
            path: 'email-otp-verify',
            pathMatch: 'full',
            component: EmailOtpVerifyContainer,
          },
          {
            path: 'choose-mfa-method',
            pathMatch: 'full',
            component: ChooseMfaMethodContainer,
          },
        ],
      },
    ]),
    AppFormsModule,
  ],
  declarations: [
    LoginRootContainer,
    LoginContainer,
    ForbiddenContainer,
    ForgotPasswordContainer,
    ResetPasswordContainer,
    SelectModuloModalContainer,
    ChangePasswordModalContainer,
    MicrosoftAuthenticatorSetupContainer,
    MicrosoftAuthenticatorVerifyContainer,
    EmailOtpVerifyContainer,
    ChooseMfaMethodContainer,
  ],
  exports: [AuthModule],
})
export class AppAuthModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: AppAuthModule
  ) {
    if (parentModule) {
      throw new Error(
        'AppAuthModule is already loaded. Import only in AppModule'
      );
    }
  }

  static forRoot(config: AuthConfig): ModuleWithProviders<AppAuthModule> {
    return {
      ngModule: AppAuthModule,
      providers: [
        AuthFacade,
        AuthService,
        {
          provide: AuthConfig,
          useValue: config,
        },
        { provide: AbstractSecurityStorage, useClass: CustomStorage },
        // {
        //   provide: APP_INITIALIZER,
        //   useFactory: configureAuth,
        //   deps: [AuthConfig],
        //   multi: true,
        // },
        httpInterceptorProviders,
      ],
    };
  }
}
